// E8RhombusSquareAreaPFClosedTask.js
// Zadanie zamkniete E8 (2025 CZE zad. 9) - pola rombu i kwadratu (P/F).
// Kwadrat K o boku a (pole a^2). Romb R: iloczyn przekatnych = prod (pole = prod/2).

import React from "react";
import { BlockMath, InlineMath } from "../../../../components/math/KatexWrappers";
import TruthInput from "../../../../components/inputs/TruthInput";

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function buildData({ a, prod, claimRomb }) {
  const squareArea = a * a;
  const rombArea = prod / 2;
  return {
    a,
    prod,
    claimRomb,
    squareArea,
    rombArea,
    correctPF: [
      claimRomb === rombArea ? "P" : "F",
      squareArea === rombArea ? "P" : "F",
    ],
  };
}

function createOriginalTask() {
  return {
    type: "E8RhombusSquareAreaPFClosedTask",
    data: buildData({ a: 8, prod: 64, claimRomb: 32 }),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 9 },
    tags: ["e8", "planimetria", "pole", "romb", "kwadrat", "prawda_falsz"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  const a = pick([6, 7, 8, 9, 10]);
  const prod = pick([48, 60, 64, 72, 84, 96, 128, 200]); // parzysty => pole rombu calkowite
  const rombArea = prod / 2;
  const s1True = Math.random() < 0.5;
  const claimRomb = s1True ? rombArea : rombArea + pick([-6, -4, 4, 6, 8]);
  return {
    type: "E8RhombusSquareAreaPFClosedTask",
    data: buildData({ a, prod, claimRomb: Math.max(1, claimRomb) }),
    source: { variant: "random", basedOn: "E8_2025_CZE_9" },
    tags: ["e8", "planimetria", "pole", "romb", "kwadrat", "prawda_falsz"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Dane są dwie figury: kwadrat <InlineMath math="K" /> i romb{" "}
        <InlineMath math="R" />. Długość boku kwadratu <InlineMath math="K" /> jest równa{" "}
        <InlineMath math={`${data.a}`} />. Iloczyn długości przekątnych rombu{" "}
        <InlineMath math="R" /> jest równy <InlineMath math={`${data.prod}`} />.
      </p>
      <p>
        Oceń prawdziwość podanych zdań. Wybierz <InlineMath math="P" />, jeśli zdanie
        jest prawdziwe, albo <InlineMath math="F" />, jeśli jest fałszywe.
      </p>
      <p>1. Pole rombu <InlineMath math="R" /> jest równe <InlineMath math={`${data.claimRomb}`} />.</p>
      <p>2. Pole kwadratu <InlineMath math="K" /> jest równe polu rombu <InlineMath math="R" />.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer || {};
  const setValue = (key, nextValue) => onChange?.({ ...value, [key]: nextValue });
  return (
    <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
      {[["s1", "1."], ["s2", "2."]].map(([key, label]) => (
        <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ minWidth: 18 }}>{label}</span>
          <TruthInput value={value[key] || null} disabled={disabled} onChange={(nextValue) => setValue(key, nextValue)} />
        </div>
      ))}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer?.s1 === data.correctPF[0] && userAnswer?.s2 === data.correctPF[1];
}

export function renderAnswer({ data }) {
  return <InlineMath math={`1.\\ \\text{${data.correctPF[0]}},\\quad 2.\\ \\text{${data.correctPF[1]}}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer?.s1 && !userAnswer?.s2) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`1.\\ \\text{${userAnswer?.s1 || "-"}},\\quad 2.\\ \\text{${userAnswer?.s2 || "-"}}`} />;
}

export function renderExplanation({ data }) {
  const { a, prod, squareArea, rombArea } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p><strong>Zdanie 1.</strong> Pole rombu to połowa iloczynu przekątnych:</p>
      <BlockMath math={`P_R=\\frac{${prod}}{2}=${rombArea}`} />
      <p>Zdanie 1. jest {data.correctPF[0] === "P" ? "prawdziwe" : "fałszywe"}.</p>
      <p><strong>Zdanie 2.</strong> Pole kwadratu:</p>
      <BlockMath math={`P_K=${a}^2=${squareArea}`} />
      <p>
        <InlineMath math={`${squareArea}${squareArea === rombArea ? "=" : "\\neq"}${rombArea}`} />,
        więc zdanie 2. jest {data.correctPF[1] === "P" ? "prawdziwe" : "fałszywe"}.
      </p>
      <p>Odpowiedź: <InlineMath math={`1.\\ \\text{${data.correctPF[0]}},\\ 2.\\ \\text{${data.correctPF[1]}}`} />.</p>
    </div>
  );
}
